import {
  HHAlert,
  HHButton,
  HHStack,
} from '@hinge-health/react-component-library';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigurationFilterBar } from '../components/configuration-filter-bar';
import { ConfigurationStatusConfirmationDialog } from '../components/configuration-status-confirmation-dialog';
import { ConfigurationsTable } from '../components/configurations-table';
import { CursorPagination } from '../components/cursor-pagination';
import { PageHeader } from '../components/page-header';
import { PARTNER_DATA_GATEWAY_ROUTES } from '../constants/partner-data-gateway.constant';
import { useBasePath } from '../context/base-path-context';
import { usePermissions } from '../context/permissions-context';
import { useConfigurationStatusTransition } from '../hooks/use-configuration-status-transition';
import { useConfigurations } from '../hooks/use-configurations';
import { useDataProviders } from '../hooks/use-data-providers';
import { ConfigurationFilters, DEFAULT_CONFIGURATION_FILTERS } from '../types';

export const CONFIGURATIONS_PAGE_TEST_ID =
  'partner-data-gateway-configurations-page';
export const CONFIGURATIONS_PAGE_NO_ACCESS_TEST_ID =
  'partner-data-gateway-configurations-page-no-access';
export const CONFIGURATIONS_PAGE_CREATE_TEST_ID =
  'partner-data-gateway-configurations-page-create-button';

/**
 * Configurations list: the entry point to the client onboarding workflow.
 *
 * Creating and editing live on their own routes rather than in a drawer so
 * a half-finished configuration survives a reload and can be shared as a
 * link — these are long forms, not quick edits.
 */
export const ConfigurationsPage = (): JSX.Element => {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const { canViewConfigurations, canManageConfigurations } = usePermissions();

  const [filters, setFilters] = useState<ConfigurationFilters>(
    DEFAULT_CONFIGURATION_FILTERS,
  );

  const {
    configurations,
    isLoading,
    hasError,
    hasNext,
    hasPrevious,
    currentPage,
    goToNext,
    goToPrevious,
    refetch,
  } = useConfigurations({
    status: filters.status ?? undefined,
    dataProvider: filters.dataProvider ?? undefined,
  });

  const { dataProviders } = useDataProviders();

  const {
    pendingTransition,
    selectTransition,
    cancelTransition,
    confirmTransition,
    isSaving,
    error: transitionError,
  } = useConfigurationStatusTransition({ onSuccess: refetch });

  if (!canViewConfigurations) {
    return (
      <HHAlert hhVariant="outlined" severity="warning">
        <span data-testid={CONFIGURATIONS_PAGE_NO_ACCESS_TEST_ID}>
          You do not have access to view partner data configurations.
        </span>
      </HHAlert>
    );
  }

  return (
    // The parent owns horizontal padding so the title and table share one
    // left edge with the ingestions list (see pages/CLAUDE.md in billing-tool).
    <HHStack
      flex={1}
      direction="column"
      gap={6}
      paddingX={6}
      paddingY={6}
      data-testid={CONFIGURATIONS_PAGE_TEST_ID}
    >
      <PageHeader
        title="Configurations"
        subtitle="How each partner's files are matched, parsed and acted on."
        actions={
          canManageConfigurations && (
            <HHButton
              hhVariant="variant-bypass"
              variant="contained"
              onClick={(): void =>
                navigate(
                  `${basePath}/${PARTNER_DATA_GATEWAY_ROUTES.configurationCreate}`,
                )
              }
              data-testid={CONFIGURATIONS_PAGE_CREATE_TEST_ID}
            >
              Create configuration
            </HHButton>
          )
        }
      />

      <ConfigurationsTable
        configurations={configurations}
        canManage={canManageConfigurations}
        disabled={isSaving}
        onStatusChange={selectTransition}
        isLoading={isLoading}
        hasError={hasError}
        toolbar={
          <ConfigurationFilterBar
            filters={filters}
            onFiltersChange={setFilters}
            dataProviders={dataProviders}
          />
        }
        footer={
          <CursorPagination
            currentPage={currentPage}
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            onPrevious={goToPrevious}
            onNext={goToNext}
          />
        }
      />

      {pendingTransition && (
        <ConfigurationStatusConfirmationDialog
          open
          targetStatus={pendingTransition.target}
          configurationName={pendingTransition.configuration.name}
          loading={isSaving}
          error={transitionError}
          onConfirm={(): void => {
            confirmTransition();
          }}
          onCancel={cancelTransition}
        />
      )}
    </HHStack>
  );
};

export default ConfigurationsPage;
